<template>
  <DialogBase
      :visible.sync="visible"
      :title="activityLogTitle"
      size="md"
      :show-confirm="false"
      cancel-text="Đóng"
      :use-panel="false"
      @cancel="handleClose"
  >
    <div class="eal-dialog">
      <!-- Header info -->
      <div class="eal-header">
        <span class="eal-student-name">{{ studentName }}</span>
        <!--        <el-tag size="mini" type="info">{{ evaluationTypeLabel }}</el-tag>-->
      </div>

      <!-- Filter -->
      <div class="eal-filter">
        <el-select
            v-model="filterAction"
            placeholder="Hành động"
            size="small"
            clearable
            style="width: 140px"
            @change="fetchLogs"
        >
          <el-option label="Tất cả" value="" />
          <el-option label="Tạo mới" value="CREATE" />
          <el-option label="Chỉnh sửa" value="UPDATE" />
          <el-option label="Xoá" value="DELETE" />
          <el-option v-if="periodic" label="Duyệt" value="APPROVE" />
        </el-select>
      </div>

      <!-- Timeline -->
      <div v-loading="loading" class="eal-timeline">
        <div v-if="logs.length === 0 && !loading" class="eal-empty">
          <i class="el-icon-document"/>
          <span>Chưa có lịch sử hoạt động</span>
        </div>
        <div
            v-for="(log, idx) in logs"
            :key="idx"
            class="eal-log-item"
        >
          <div class="eal-log-dot" :class="actionDotClass(log.action)"/>
          <div class="eal-log-content">
            <div class="eal-log-text">
              <span class="eal-log-actor">{{ log.actorName }}</span>
              <el-tag size="mini" :type="roleTagType(log.actorRole)">{{ roleLabel(log.actorRole) }}</el-tag>
              <span>đã {{ actionLabel(log.action) }} {{ typeLabel(log.evaluationType) }}</span>
              <span v-if="log.evaluationType === 'REPLY' && log.parentEvaluationType" class="eal-log-parent">
                cho {{ typeLabel(log.parentEvaluationType) }}
              </span>
            </div>
            <div class="eal-log-meta">
              <span class="eal-log-time">{{ formatTimestamp(log.timestamp) }}</span>
              <span v-if="log.evaluationType === 'WEEKLY' && log.weekNumber" class="eal-log-date">
                · Tuần {{ log.weekNumber }} ({{ formatDate(log.evaluationDate) }} – {{ formatDate(log.toDate) }})
              </span>
              <span v-else-if="log.evaluationDate" class="eal-log-date">
                · Ngày: {{ formatDate(log.evaluationDate) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DialogBase>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import EvaluationService from "@/modules/evaluation/services/EvaluationService";
import PeriodicEvaluationService from "@/modules/evaluation/services/PeriodicEvaluationService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import moment from "moment";

const ACTION_LABELS = { CREATE: "tạo", UPDATE: "chỉnh sửa", DELETE: "xoá", APPROVE: "duyệt" };
const TYPE_LABELS = { DAILY: "nhận xét ngày", WEEKLY: "nhận xét tuần", MONTHLY: "nhận xét tháng", PERIODIC: "nhận xét định kỳ", REPLY: "phản hồi" };
const ROLE_LABELS = { teacher: "Giáo viên", parent: "Phụ huynh", plus: "Nhà trường" };
const ROLE_TAG_TYPE = { TEACHER: "", PARENT: "warning", SCHOOL: "success" };
const TYPE_TITLE = { "DAILY": "nhận xét ngày", "WEEKLY": "nhận xét tuần", "MONTHLY": "nhận xét tháng", "PERIODIC": "nhận xét định kỳ" };

export default {
  name: "EvaluationActivityLogDialog",
  components: {DialogBase},
  props: {
    classId: { type: [Number, String], default: "" },
    periodic: { type: Boolean, default: false },
    year: { type: [Number, String], default: "" },
  },
  data() {
    return {
      visible: false,
      loading: false,
      studentId: null,
      studentName: "",
      evaluationUid: null,
      date: "",
      evaluationType: "",
      filterAction: "",
      logs: [],
    };
  },
  computed: {
    activityLogTitle() {
      return `Lịch sử thao tác ${TYPE_TITLE[this.evaluationType] || "nhận xét"}`;
    },
    evaluationTypeLabel() {
      if (this.evaluationUid) return "Đơn nhận xét";
      return TYPE_LABELS[this.evaluationType] || "Tất cả";
    },
  },
  methods: {
    /**
     * Mở dialog xem log cho 1 học sinh
     * @param {Object} params - { studentId, studentName, evaluationUid?, date, evaluationType? }
     */
    open(params) {
      this.studentId = params.studentId;
      this.studentName = params.studentName;
      this.evaluationUid = params.evaluationUid || null;
      this.date = params.date;
      this.evaluationType = params.evaluationType || "";
      this.filterAction = "";
      this.logs = [];
      this.visible = true;
      this.fetchLogs();
    },

    async fetchLogs() {
      this.loading = true;
      try {
        if (this.periodic) {
          const res = await PeriodicEvaluationService.getActivityLog({
            classId: this.classId,
            studentId: this.studentId,
            year: Number(this.year),
            evaluationUid: this.evaluationUid || undefined,
            action: this.filterAction || undefined,
          });
          this.logs = res.data.data || [];
          return;
        }
        const params = {
          classId: this.classId,
          studentId: this.studentId,
          date: this._getRequestDate(),
        };
        if (this.evaluationUid) {
          params.evaluationUid = this.evaluationUid;
        }
        if (this.evaluationType) {
          params.evaluationType = this.evaluationType;
        }
        if (this.filterAction) {
          params.action = this.filterAction;
        }
        const res = await EvaluationService.getActivityLog(params);
        const logs = res.data.data || [];
        this.logs = this.evaluationType
            ? logs.filter(log => this.isLogOfSelectedType(log))
            : logs;
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
            err?.response?.data?.message || "Không thể tải lịch sử hoạt động!"
        );
        this.logs = [];
      } finally {
        this.loading = false;
      }
    },

    _getRequestDate() {
      if (this.evaluationType !== "MONTHLY") return this.date;
      return moment(this.date, "YYYY-MM", true).startOf("month").format("YYYY-MM-DD");
    },

    isLogOfSelectedType(log) {
      return log.evaluationType === this.evaluationType
          || (log.evaluationType === "REPLY" && log.parentEvaluationType === this.evaluationType);
    },

    actionLabel(action) {
      return ACTION_LABELS[action] || action;
    },
    typeLabel(type) {
      return TYPE_LABELS[type] || type;
    },
    roleLabel(role) {
      return ROLE_LABELS[role] || role;
    },
    roleTagType(role) {
      return ROLE_TAG_TYPE[role] || "";
    },

    titleLabel() {
      return TYPE_TITLE[this.evaluationType] || "";
    },

    actionDotClass(action) {
      if (action === "CREATE") return "eal-log-dot--create";
      if (action === "UPDATE") return "eal-log-dot--update";
      if (action === "DELETE") return "eal-log-dot--delete";
      return "";
    },
    formatTimestamp(ts) {
      if (!ts) return "";
      const dt = ts.split("T");
      if (dt.length === 2) {
        const parts = dt[0].split("-");
        return `${dt[1].substring(0, 5)} ${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return ts;
    },
    formatDate(val) {
      if (!val) return "";
      const parts = val.split("-");
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
      return val;
    },
    handleClose() {
      this.visible = false;
      this.logs = [];
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog";

.eal-dialog {
  padding: 4px 0;
}

.eal-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.eal-student-name {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.eal-filter {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.eal-timeline {
  min-height: 120px;
  max-height: 400px;
  overflow-y: auto;
}

.eal-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 0;
  color: #94a3b8;
  font-size: 14px;

  i {
    font-size: 20px;
  }
}

.eal-log-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
}

.eal-log-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 5px;
  flex-shrink: 0;
  background: #94a3b8;
}

.eal-log-dot--create {
  background: #22c55e;
}

.eal-log-dot--update {
  background: #3b82f6;
}

.eal-log-dot--delete {
  background: #ef4444;
}

.eal-log-content {
  flex: 1;
  min-width: 0;
}

.eal-log-text {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 13px;
  color: #334155;
  line-height: 1.6;
}

.eal-log-actor {
  font-weight: 600;
  color: #1e293b;
}

.eal-log-parent {
  color: #64748b;
  font-style: italic;
}

.eal-log-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #94a3b8;
}

.eal-log-time {
  font-weight: 500;
}

.eal-log-date {
  margin-left: 4px;
}
</style>
